
    import __i18nConfig from '@next-translate-root/i18n.js'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useMemo } from 'react';
import { type GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import ezCaterLogo from '@/assets/images/brand/ezcater-vertical-lg.png';
import heroLeftAvif from '@/assets/images/home/keywordAndLocationHero/left_image.avif';
import heroLeftPng from '@/assets/images/home/keywordAndLocationHero/left_image.png';
import heroLeftWebp from '@/assets/images/home/keywordAndLocationHero/left_image.webp';
import heroRightAvif from '@/assets/images/home/keywordAndLocationHero/right_image.avif';
import heroRightPng from '@/assets/images/home/keywordAndLocationHero/right_image.png';
import heroRightWebp from '@/assets/images/home/keywordAndLocationHero/right_image.webp';
import HomeRoot from '@/components/features/home/HomeRoot/HomeRoot';
import EppoFlags from '@/EppoFlags';
import { type FulfillmentDetailAddressInput, type LoggedOutHomePageQuery, LoggedOutHomePageQueryDocument, } from '@/graphql/types';
import getLandingPagePath from '@/pageApi/getLandingPagePath';
import initServerSideRequest from '@/pageApi/initServerSideRequest';
import { ROOT_PATH } from '@/paths';
import { compilePath } from '@/utils';
import { fullUrlForPath } from '@/utils/compilePath';
import { geocodedAddressResultToFulfillmentDetailAddressInput } from '@/utils/geocodedAddressResult';
import serializeJsonForScriptTag from '@/utils/serializeJsonForScriptTag';
const config = getConfig();
const publicRuntimeConfig = config?.publicRuntimeConfig;
type HomeProps = {
    imageSupport: {
        avif: boolean;
        webp: boolean;
    };
    geoipRecommendedDefaultAddress?: FulfillmentDetailAddressInput | null;
};
const Home: React.FC<HomeProps> = ({ imageSupport, geoipRecommendedDefaultAddress }) => {
    const { t } = useTranslation('home');
    const heroLeftImage = useMemo(() => {
        if (imageSupport.avif)
            return heroLeftAvif;
        if (imageSupport.webp)
            return heroLeftWebp;
        return heroLeftPng;
    }, [imageSupport]);
    const heroRightImage = useMemo(() => {
        if (imageSupport.avif)
            return heroRightAvif;
        if (imageSupport.webp)
            return heroRightWebp;
        return heroRightPng;
    }, [imageSupport]);
    return (<>
      <Head>
        <title>{t('name')}</title>
        <meta name="description" content={t('pageDescription')}/>
        <link rel="canonical" href={fullUrlForPath(ROOT_PATH)}/>
        {publicRuntimeConfig?.facebookDomainVerificationCode && (<meta name="facebook-domain-verification" content={publicRuntimeConfig.facebookDomainVerificationCode}/>)}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: serializeJsonForScriptTag({
                '@context': 'https://schema.org',
                '@type': 'Corporation',
                '@id': 'https://www.ezcater.com/#organization',
                name: 'ezCater',
                url: 'https://www.ezcater.com',
                logo: ezCaterLogo.src,
                description: 'ezCater is the #1 food tech platform for workplaces in the US. The company makes it easy for any organization to manage its food needs and order from over 100,000 restaurants nationwide.',
                sameAs: [
                    'https://www.linkedin.com/company/ezcater',
                    'https://www.instagram.com/ezcater/',
                    'https://www.facebook.com/ezCaterInc/',
                    'https://www.tiktok.com/discover/Ezcater',
                    'https://www.youtube.com/c/ezCater',
                    'https://play.google.com/store/apps/details?id=com.ezcater.ezcaterapp',
                    'https://apps.apple.com/us/app/ezcater-business-catering/id1162865183',
                ],
                location: {
                    '@type': 'Place',
                    name: 'ezCater Headquarters',
                    address: {
                        '@type': 'PostalAddress',
                        streetAddress: '40 Water Street, 5th Floor',
                        addressLocality: 'Boston',
                        addressRegion: 'MA',
                        postalCode: '02109',
                        addressCountry: 'US',
                    },
                },
                contactPoint: {
                    '@type': 'ContactPoint',
                    telephone: '+1-800-488-1803',
                    contactType: 'customer support',
                    areaServed: 'US',
                },
            }),
        }}/>
        <link rel="preload" href={heroLeftImage.src} as="image"/>
        <link rel="preload" href={heroRightImage.src} as="image"/>
      </Head>
      <HomeRoot geoipRecommendedDefaultAddress={geoipRecommendedDefaultAddress}/>
    </>);
};
const getServerSideProps: GetServerSideProps<StoreNextPageDataProps & HomeProps> = async (ctx) => {
    const { apolloClient, extractAppProps, loadAssignments } = initServerSideRequest(ctx);
    const { data: userData } = await apolloClient.query<LoggedOutHomePageQuery>({
        query: LoggedOutHomePageQueryDocument,
        errorPolicy: 'all',
    });
    const isLoggedIn = Boolean(userData?.me?.consumerAccount);
    const { getStringAssignment } = loadAssignments();
    if (isLoggedIn) {
        // redirect to RHP — /home for the personalized home page treatment (ezx570)
        return {
            redirect: {
                destination: compilePath(getLandingPagePath({ getStringAssignment }), {}, ctx.query),
                permanent: false,
            },
        };
    }
    getStringAssignment({
        flagKey: EppoFlags.IntercomHelpCenter,
        defaultValue: 'fallback',
    });
    getStringAssignment({
        flagKey: EppoFlags.FixOurFoundationsNavbarPhase2a,
        defaultValue: 'fallback',
    });
    getStringAssignment({
        flagKey: EppoFlags.FixOurFoundationsNavbarPhase2b,
        defaultValue: 'fallback',
    });
    getStringAssignment({
        flagKey: EppoFlags.JumpBackInHomepageV2,
        defaultValue: 'fallback',
    });
    getStringAssignment({
        flagKey: EppoFlags.GroupOrderingPhase1,
        defaultValue: 'fallback',
    });
    getStringAssignment({
        flagKey: EppoFlags.PreciseAddressNudge,
        defaultValue: 'fallback',
    });
    getStringAssignment({
        flagKey: EppoFlags.ValueProps,
        defaultValue: 'fallback',
    });
    return {
        props: {
            ...extractAppProps(),
            geoipRecommendedDefaultAddress: userData?.identity?.recommendedDefaultAddress
                ? geocodedAddressResultToFulfillmentDetailAddressInput(userData.identity.recommendedDefaultAddress)
                : null,
            imageSupport: {
                avif: ctx.req.headers.accept?.includes('image/avif') ?? false,
                webp: ctx.req.headers.accept?.includes('image/webp') ?? false,
            },
            libertyWebchat: {
                enabled: true,
            },
        },
    };
};
export default Home;

    async function __Next_Translate__getServerSideProps__1a1a789f7c0__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/index',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a1a789f7c0__ as getServerSideProps }
  